import {Button, Combobox, InputBase, Stack, Text, useCombobox} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {useEffect, useState} from 'react'

import type {CustomerDataLayer} from './customer-data-layer'
import {CustomerSummary} from './customer-summary'
import {CustomerFormModal} from './new-customer-modal'
import type {Customer} from './types'

type CustomerAutocompleteProps = {
    value: string | null
    error?: string
    dataLayer: CustomerDataLayer
    onChange: (customerId: string | null, customerName: string | null) => void
}

type CustomerModalState = {open: false} | {open: true; mode: 'create'}

export function CustomerAutocomplete({
    value,
    error,
    dataLayer,
    onChange,
}: CustomerAutocompleteProps) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })

    const [search, setSearch] = useState('')
    const [debouncedSearch] = useDebouncedValue(search, 300)
    const [options, setOptions] = useState<Customer[]>([])
    const [loadedSearch, setLoadedSearch] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [modalState, setModalState] = useState<CustomerModalState>({open: false})

    const selectedCustomer = value ? dataLayer.getCustomerById(value) : undefined

    useEffect(() => {
        if (!value) {
            return
        }
        const customer = dataLayer.getCustomerById(value)
        if (customer) {
            setSearch(customer.name)
            setOptions([customer])
        }
    }, [value, dataLayer])

    useEffect(() => {
        if (selectedCustomer) {
            return
        }

        let cancelled = false

        async function loadOptions() {
            setIsLoading(true)
            try {
                const results = await dataLayer.listCustomers({name: debouncedSearch})
                if (!cancelled) {
                    setOptions(results)
                    setLoadedSearch(debouncedSearch)
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        void loadOptions()

        return () => {
            cancelled = true
        }
    }, [debouncedSearch, dataLayer, selectedCustomer])

    const showCreateOption =
        !selectedCustomer &&
        loadedSearch === debouncedSearch &&
        debouncedSearch.trim().length > 0 &&
        !isLoading &&
        !options.some(
            (customer) => customer.name.toLowerCase() === debouncedSearch.trim().toLowerCase(),
        )

    function handleSelect(customer: Customer) {
        setSearch(customer.name)
        setOptions([customer])
        onChange(customer.id, customer.name)
        combobox.closeDropdown()
    }

    function handleSaved(customer: Customer) {
        handleSelect(customer)
    }

    function handleChangeCustomer() {
        setSearch('')
        setOptions([])
        onChange(null, null)
    }

    return (
        <>
            {selectedCustomer ? (
                <CustomerSummary
                    customer={selectedCustomer}
                    editHref={`/customers/${selectedCustomer.id}`}
                    onChangeCustomer={handleChangeCustomer}
                />
            ) : (
                <Stack gap="sm">
                    <Combobox
                        store={combobox}
                        onOptionSubmit={(optionValue) => {
                            if (optionValue === '__create__') {
                                setModalState({open: true, mode: 'create'})
                                combobox.closeDropdown()
                                return
                            }
                            const customer = options.find((item) => item.id === optionValue)
                            if (customer) {
                                handleSelect(customer)
                            }
                        }}
                    >
                        <Combobox.Target>
                            <InputBase
                                label="Search customers"
                                placeholder="Type to search customers"
                                value={search}
                                error={error}
                                rightSection={isLoading ? '…' : undefined}
                                onChange={(event) => {
                                    setSearch(event.currentTarget.value)
                                    onChange(null, null)
                                    combobox.openDropdown()
                                    combobox.updateSelectedOptionIndex()
                                }}
                                onClick={() => combobox.openDropdown()}
                                onFocus={() => combobox.openDropdown()}
                            />
                        </Combobox.Target>

                        <Combobox.Dropdown>
                            <Combobox.Options>
                                {options.map((customer) => (
                                    <Combobox.Option
                                        key={customer.id}
                                        value={customer.id}
                                        onMouseDown={(event) => event.preventDefault()}
                                    >
                                        <Text size="sm">{customer.name}</Text>
                                    </Combobox.Option>
                                ))}
                                {showCreateOption && (
                                    <Combobox.Option
                                        value="__create__"
                                        aria-label={`Create customer "${debouncedSearch.trim()}"`}
                                        onMouseDown={(event) => event.preventDefault()}
                                    >
                                        Create customer &quot;{debouncedSearch.trim()}&quot;
                                    </Combobox.Option>
                                )}
                                {!isLoading && options.length === 0 && !showCreateOption && (
                                    <Combobox.Empty>No customers found</Combobox.Empty>
                                )}
                            </Combobox.Options>
                        </Combobox.Dropdown>
                    </Combobox>

                    {showCreateOption && (
                        <Button
                            size="md"
                            variant="filled"
                            type="button"
                            aria-label={`Create customer "${debouncedSearch.trim()}"`}
                            onClick={() => setModalState({open: true, mode: 'create'})}
                        >
                            Create customer &quot;{debouncedSearch.trim()}&quot;
                        </Button>
                    )}
                </Stack>
            )}

            <CustomerFormModal
                opened={modalState.open}
                mode="create"
                initialName={debouncedSearch.trim()}
                onClose={() => setModalState({open: false})}
                onCreate={dataLayer.createCustomer}
                onUpdate={dataLayer.updateCustomer}
                onSaved={handleSaved}
            />
        </>
    )
}
